import React, {useMemo, useRef, useState} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import {FaCheck, FaTrash, FaUsers} from "react-icons/fa";
import {FaHouse, FaPenToSquare, FaTaxi, FaTriangleExclamation} from "react-icons/fa6";
import {AgGridReact} from "ag-grid-react";
import Button from "../../../../../../components/button/button.jsx";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import ScheduleConflictModal from "./schedule-conflict-modal.jsx";
import {composeAddress, conflictInfo, scheduleTypeLabel} from "../../utils/schedule-utils.js";

const TYPE_ICON = {VISIT: FaHouse, PATROL: FaTaxi};

const TONES = {
    neutral: "bg-gray-100 text-gray-500",
    ok: "bg-green-100 text-green-700",
    pend: "bg-orange-100 text-[#fbae1d]",
    primary: "bg-blue-50 text-[#063A6C]",
};

const CountContent = ({icon: IconComponent, tone, value, label}) => (
    <div className={"flex items-center gap-3 px-4 py-3 min-w-46.25"}>
        <div className={["w-9 h-9 rounded-lg grid place-items-center shrink-0", TONES[tone]].join(" ")}>
            <IconComponent size={18}/>
        </div>
        <div>
            <div className={"text-xl font-extrabold leading-none text-gray-800"}>{value}</div>
            <div className={"text-xs text-gray-500 mt-0.5"}>{label}</div>
        </div>
    </div>
);

const ScheduleConfirmationStep = ({schedule, citizens, addressSelection, timeConfirmed, onResolve, onRemove}) => {
    const [modalCitizen, setModalCitizen] = useState(undefined);

    const confirmRemove = (row) => {
        projectUtils.swalMessage(
            "warning",
            "Remover",
            `Remover "${row.name ?? ""}" do agendamento?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) onRemove(row.id);
            }
        );
    };

    const handlersRef = useRef({});
    handlersRef.current = {openModal: setModalCitizen, remove: confirmRemove};

    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const rows = useMemo(() => citizens.map(citizen => {
        const selectedIds = addressSelection[citizen.id] ?? [];
        const {matches, selectedAddresses, noEligibleAddress, needsChoice, hasConflict} = conflictInfo(
            citizen, schedule.type, schedule, selectedIds, timeConfirmed[citizen.id]
        );

        let addressText = "—";
        if (noEligibleAddress) addressText = `Nenhum endereço habilitado para ${scheduleTypeLabel(schedule.type).toLowerCase()}`;
        else if (selectedAddresses.length === 1) addressText = composeAddress(selectedAddresses[0]);
        else if (selectedAddresses.length > 1) addressText = `${selectedAddresses.length} endereços selecionados`;

        const canEdit = !hasConflict && matches.length > 1;
        const canResolve = hasConflict && !noEligibleAddress;
        const ready = !hasConflict && selectedIds.length > 0;

        return {
            id: citizen.id,
            name: citizen.name,
            addressText,
            invalidAddress: needsChoice || noEligibleAddress,
            hasConflict,
            canEdit,
            canResolve,
            ready,
            status: hasConflict ? "Pendente" : "OK",
            actionKey: canResolve ? "conflict" : canEdit ? "edit" : "none",
            citizen,
        };
    }), [citizens, schedule, addressSelection, timeConfirmed]);

    const readyCount = rows.filter(r => r.ready).length;
    const pendingCount = rows.length - readyCount;
    const ScheduleIcon = TYPE_ICON[schedule.type] ?? FaUsers;

    const [columnDefs] = useState([
        {field: "name", headerName: "Nome do protegido", flex: 1, comparator: aggridUtils.nullLastComparator},
        {field: "addressText", headerName: "Endereço", flex: 2, sortable: false},
        {
            field: "status", headerName: "Conflitos", width: 140, sortable: false,
            cellRenderer: ({data}) => (
                <div className={"flex items-center justify-center h-full"}>
                    <span className={[
                        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold",
                        data.hasConflict ? "bg-orange-100 text-[#e19300]" : "bg-green-100 text-green-700"
                    ].join(" ")}>
                        {data.hasConflict ? "Pendente" : "OK"}
                    </span>
                </div>
            )
        },
        {
            headerName: "Ações", field: "actionKey", width: 130, sortable: false,
            cellRenderer: ({data}) => (
                <div className={"flex items-center justify-center gap-2 h-full"}>
                    {
                        data.canResolve
                            ? <Button target={"warning"} className={"w-7.5! h-6.25! p-0!"} title={"Resolver conflitos"} onClick={() => handlersRef.current.openModal(data.citizen)}>
                                <FaTriangleExclamation/>
                            </Button>
                            : data.canEdit ?
                            <Button className={"w-7.5! h-6.25! p-0!"} title={"Alterar endereços"} onClick={() => handlersRef.current.openModal(data.citizen)}>
                                <FaPenToSquare/>
                            </Button> :
                            <span className={"w-7.5! h-6.25! p-0!"} />
                    }
                    <Button target={"danger"} className={"w-7.5! h-6.25! p-0!"} title={"Remover"} onClick={() => handlersRef.current.remove(data)}>
                        <FaTrash/>
                    </Button>
                </div>
            )
        },
    ]);

    return (
        <>
            <div className={"flex flex-wrap items-stretch gap-3"}>
                <div className={"flex items-center gap-3 bg-white border border-gray-200 rounded-lg shadow px-4 py-3 min-w-55"}>
                    <div className={["w-9 h-9 rounded-lg grid place-items-center shrink-0", TONES.primary].join(" ")}>
                        <ScheduleIcon size={18}/>
                    </div>
                    <div>
                        <div className={"text-sm font-bold text-gray-800"}>{scheduleTypeLabel(schedule.type) || "—"} · {schedule.date || "—"}</div>
                        <div className={"text-xs text-gray-500 mt-0.5"}>{schedule.startTime} – {schedule.endTime}</div>
                    </div>
                </div>

                <div className={"flex items-center bg-white border border-gray-200 rounded-lg shadow min-w-50"}>
                    <CountContent icon={FaUsers} tone={"neutral"} value={rows.length} label={"Protegidos no agendamento"}/>
                </div>

                <div className={"flex items-stretch bg-white border border-gray-200 rounded-lg shadow"}>
                    <CountContent icon={FaCheck} tone={"ok"} value={readyCount} label={"Prontos para salvar"}/>
                    <div className={"w-px bg-gray-200 my-2"}/>
                    <CountContent icon={FaTriangleExclamation} tone={"pend"} value={pendingCount} label={"Pendentes de resolução"}/>
                </div>
            </div>

            <Card>
                <Card.Header>
                    <Card.Title>Agendamento</Card.Title>
                </Card.Header>

                <Card.Body>
                    <div className={"h-[calc(100vh-323px)]"}>
                        <AgGridReact
                            rowData={rows}
                            columnDefs={columnDefs}
                            getRowId={({data}) => String(data.id)}
                            pagination
                            paginationPageSize={100}
                            paginationPageSizeSelector={[20, 50, 100, 500]}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={theme}
                        />
                    </div>
                </Card.Body>
            </Card>

            <ScheduleConflictModal
                open={!!modalCitizen}
                setOpen={() => setModalCitizen(undefined)}
                citizen={modalCitizen}
                schedule={schedule}
                selectedIds={modalCitizen ? (addressSelection[modalCitizen.id] ?? []) : []}
                confirmed={modalCitizen ? timeConfirmed[modalCitizen.id] : false}
                onConfirm={onResolve}
            />
        </>
    );
};

export default ScheduleConfirmationStep;
